<template>
  <SIDetailPage
    :pr="pr"
    :locale="localeValue"
    :get-image-url="getImageUrl"
    :contact-text="contactText"
  >
    <template #afterScenarios>
      <section :class="['agv-block', { 'locale-ja': localeValue === 'ja' }]">
        <div class="inner">
          <SISectionTitle :title="pr.agvTitle" :subtitle="pr.agvTitleEn" />
          <div v-viewportChanged="showView" class="hidden-element steps-row">
            <template v-for="(step, index) in pr.agvSteps" :key="step.step">
              <article class="step-card">
                <div class="step-no">{{ step.step }}</div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </article>
              <div v-if="index < pr.agvSteps.length - 1" class="step-line"></div>
            </template>
          </div>

          <div v-viewportChanged="showView" class="hidden-element arch-card">
            <h4>{{ pr.agvArchitecture }}</h4>
            <div class="arch-row">
              <div class="arch-node">
                <span>{{ pr.agvWms }}</span>
                <small>{{ pr.agvWmsLabel }}</small>
              </div>
              <span class="arrow">←→</span>
              <div class="arch-node">
                <span>{{ pr.agvController }}</span>
                <small>{{ pr.agvControllerLabel }}</small>
              </div>
              <span class="arrow">←→</span>
              <div class="arch-node">
                <span>{{ pr.agvFleet }}</span>
                <small>{{ pr.agvFleetLabel }}</small>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </SIDetailPage>
</template>

<script>
import { computed, inject } from 'vue';
import usePageResource from '@/composables/usePageResource';
import useRenderData from '@/composables/useRenderData';
import SIDetailPage from '@/components/si/SIDetailPage.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';

export default {
  name: 'wms-page',
  components: { SIDetailPage, SISectionTitle },
  setup() {
    const { pr, getImageUrl } = useRenderData('wms');
    const { pr: works } = usePageResource('works');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactText = computed(() => works.value.contact);

    return {
      pr,
      getImageUrl,
      localeValue,
      contactText,
    };
  },
};
</script>

<style lang="scss" scoped>
.agv-block {
  background: #f7f9fa;
  padding: 82px 0;

  .inner {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    box-sizing: border-box;
  }

  .steps-row {
    display: flex;
    align-items: stretch;
    margin-bottom: 28px;
  }

  .step-card {
    flex: 1;
    border: 1px solid #e4e9eb;
    background: #ffffff;
    padding: 22px;
    text-align: center;

    .step-no {
      font-size: 28px;
      line-height: 34px;
      color: #e4e9eb;
      font-weight: 700;
    }

    h4 {
      margin: 12px 0 8px;
      color: #212121;
      font-size: 16px;
      line-height: 24px;
    }

    p {
      margin: 0;
      color: #666666;
      font-size: 14px;
      line-height: 22px;
    }
  }

  .step-line {
    width: 28px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 2px;
      right: 2px;
      top: calc(50% - 1px);
      border-top: 2px solid #e4e9eb;
    }
  }

  .arch-card {
    border: 1px solid #e4e9eb;
    background: #ffffff;
    padding: 24px;

    h4 {
      margin: 0 0 16px;
      text-align: center;
      color: #212121;
      font-size: 14px;
      line-height: 20px;
    }

    .arch-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .arch-node {
      background: #f1f3f4;
      min-width: 170px;
      padding: 12px;
      text-align: center;

      span {
        display: block;
        color: #212121;
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }

      small {
        display: block;
        margin-top: 2px;
        color: #74787a;
        font-size: 12px;
        line-height: 18px;
      }
    }

    .arrow {
      color: #a7aeb1;
      font-size: 18px;
      line-height: 18px;
      font-style: normal;
    }
  }
}

.agv-block.locale-ja {
  h2,
  h3,
  h4,
  p,
  span,
  small {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

@media only screen and (max-width: 768px) {
  .agv-block {
    padding: 56px 0;

    .inner {
      padding: 0 12px;
    }

    .steps-row {
      flex-direction: column;
      gap: 10px;
    }

    .step-line {
      display: none;
    }

    .arch-card {
      padding: 24px;

      .arch-row {
        flex-direction: column;
        gap: 16px;
      }

      .arch-node {
        width: auto;
        min-width: 170px;
      }

      .arrow {
        display: none;
      }
    }
  }
}
</style>
